import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit2, RefreshCw, Eye, EyeOff, Webhook, Power, Lock } from "lucide-react";
import { verifyAdminPin } from "@/lib/api/staff-pin.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  getWhatsappConfig,
  saveWhatsappConfig,
  checkInstanceStatus,
  connectInstance,
  disconnectInstance,
  setEvolutionWebhook,
  getEvolutionWebhook,
  cleanupOtherInstances,
  resetInstance,

  fetchQrCode,
  listTemplates,
  saveTemplate,
  deleteTemplate,
  toggleTemplate,
  listAutomations,
  saveAutomation,
  deleteAutomation,
  toggleAutomation,
} from "@/lib/api/whatsapp.functions";

export const Route = createFileRoute("/_authenticated/m/automacao-config")({
  component: Page,
});

function Page() {
  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold tracking-tight">Automação e Configuração WhatsApp</h1>
      <Tabs defaultValue="automation">
        <TabsList>
          <TabsTrigger value="automation">Automação</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="config">Configuração e Conexão</TabsTrigger>
        </TabsList>
        <TabsContent value="automation" className="mt-6">
          <AutomationTab />
        </TabsContent>
        <TabsContent value="templates" className="mt-6">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="config" className="mt-6">
          <ConfigTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ AUTOMATION ============
function AutomationTab() {
  const _list = useServerFn(listAutomations);
  const _listT = useServerFn(listTemplates);
  const _save = useServerFn(saveAutomation);
  const _del = useServerFn(deleteAutomation);
  const _tog = useServerFn(toggleAutomation);

  const [rules, setRules] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ event_type: "RENEWAL_BEFORE", days: 3, template_id: "", is_active: true });

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([_list(), _listT()]);
      setRules(r as any[]);
      setTemplates((t as any[]).filter((x) => x.is_active));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const handleSave = async () => {
    if (!form.template_id) return toast.error("Selecione um template");
    setSaving(true);
    try {
      await _save({ data: form });
      toast.success("Regra criada");
      setForm({ event_type: "RENEWAL_BEFORE", days: 3, template_id: "", is_active: true });
      refresh();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Nova Regra de Automação</CardTitle>
          <CardDescription>Configure o envio automático de mensagens de cobrança/renovação.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Momento do Envio</Label>
            <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="RENEWAL_BEFORE">Antes do vencimento</SelectItem>
                <SelectItem value="RENEWAL_DUE">No dia do vencimento</SelectItem>
                <SelectItem value="RENEWAL_AFTER">Após o vencimento</SelectItem>
                <SelectItem value="RENEWAL_DAILY_AFTER">Diariamente após vencimento</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.event_type !== "RENEWAL_DUE" && (
            <div className="space-y-2">
              <Label>Quantidade de Dias</Label>
              <Input type="number" min={1} value={form.days}
                onChange={(e) => setForm({ ...form, days: parseInt(e.target.value) || 1 })} />
            </div>
          )}
          <div className="space-y-2">
            <Label>Template de Mensagem</Label>
            <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
              <SelectTrigger><SelectValue placeholder="Selecione um template..." /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Adicionar Regra
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Regras Ativas</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : rules.length === 0 ? (
            <div className="text-center py-10 px-4 bg-muted/20 rounded-lg border border-dashed">
              <p className="text-sm text-muted-foreground">Nenhuma regra configurada.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((a) => (
                <div key={a.id} className="p-3 border rounded-lg flex items-center justify-between group">
                  <div>
                    <h4 className="font-medium text-sm">
                      {a.event_type === "RENEWAL_BEFORE" && `${a.days} dias antes do vencimento`}
                      {a.event_type === "RENEWAL_AFTER" && `${a.days} dias após o vencimento`}
                      {a.event_type === "RENEWAL_DAILY_AFTER" && `Diário (até ${a.days} dias após)`}
                      {a.event_type === "RENEWAL_DUE" && "No dia do vencimento"}
                    </h4>
                    <p className="text-xs text-muted-foreground">Template: {a.whatsapp_message_templates?.name ?? "—"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={a.is_active} onCheckedChange={async (c) => { await _tog({ data: { id: a.id, is_active: c } }); refresh(); }} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                      onClick={async () => { if (confirm("Excluir?")) { await _del({ data: { id: a.id } }); refresh(); } }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ TEMPLATES ============
function TemplatesTab() {
  const _list = useServerFn(listTemplates);
  const _save = useServerFn(saveTemplate);
  const _del = useServerFn(deleteTemplate);
  const _tog = useServerFn(toggleTemplate);

  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const empty = {
    name: "",
    message: "",
    trigger_event: "MANUAL",
    target_audience: "ALL",
    is_active: true,
    stop_condition: "envio_unico" as "pago" | "envio_unico" | "max_envios" | "data_limite",
    stop_max_sends: 3,
    stop_date: "",
  };
  const [form, setForm] = useState(empty);

  const refresh = async () => {
    setLoading(true);
    try { setList((await _list()) as any[]); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const handleSave = async () => {
    if (!form.name.trim() || !form.message.trim()) return toast.error("Preencha nome e mensagem");
    setSaving(true);
    try {
      await _save({ data: {
        ...form,
        id: editingId ?? undefined,
        stop_max_sends: form.stop_condition === "max_envios" ? Number(form.stop_max_sends) || 1 : null,
        stop_date: form.stop_condition === "data_limite" && form.stop_date ? new Date(form.stop_date).toISOString() : null,
      } });
      toast.success(editingId ? "Template atualizado" : "Template criado");
      setForm(empty); setEditingId(null); refresh();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{editingId ? "Editar Template" : "Novo Template"}</CardTitle>
          <CardDescription>Crie mensagens padronizadas para envio automático ou manual.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Nome do Template</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Cobrança Mensalidade" />
          </div>
          <div className="space-y-2">
            <Label>Gatilho Automático</Label>
            <Select value={form.trigger_event} onValueChange={(v) => setForm({ ...form, trigger_event: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MANUAL">Envio Manual</SelectItem>
                <SelectItem value="NEW_ENROLLMENT">Nova Matrícula</SelectItem>
                <SelectItem value="PAYMENT_CONFIRMED">Pagamento Confirmado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Encerrar após</Label>
            <Select value={form.stop_condition} onValueChange={(v: any) => setForm({ ...form, stop_condition: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pago">Pago (parar quando aluno quitar)</SelectItem>
                <SelectItem value="envio_unico">Envio único</SelectItem>
                <SelectItem value="max_envios">Número máximo de envios</SelectItem>
                <SelectItem value="data_limite">Data limite</SelectItem>
              </SelectContent>
            </Select>
            {form.stop_condition === "max_envios" && (
              <Input type="number" min={1} value={form.stop_max_sends}
                onChange={(e) => setForm({ ...form, stop_max_sends: parseInt(e.target.value) || 1 })}
                placeholder="Quantos envios no máximo" />
            )}
            {form.stop_condition === "data_limite" && (
              <Input type="date" value={form.stop_date}
                onChange={(e) => setForm({ ...form, stop_date: e.target.value })} />
            )}
          </div>
          <div className="space-y-2">
            <Label>Mensagem</Label>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Olá {responsavel}, o plano do(a) {aluno}..." className="min-h-[120px]" />
            <p className="text-xs text-muted-foreground">
              Variáveis: <code className="bg-muted px-1 rounded">{`{responsavel}`}</code>,{" "}
              <code className="bg-muted px-1 rounded">{`{aluno}`}</code>,{" "}
              <code className="bg-muted px-1 rounded">{`{plano}`}</code>,{" "}
              <code className="bg-muted px-1 rounded">{`{valor}`}</code>,{" "}
              <code className="bg-muted px-1 rounded">{`{vencimento}`}</code>,{" "}
              <code className="bg-muted px-1 rounded">{`{link_pagamento}`}</code>
            </p>
          </div>
          <div className="flex items-center justify-between">
            <Label>Ativo</Label>
            <Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingId ? "Salvar Alterações" : "Criar Template"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={() => { setEditingId(null); setForm(empty); }}>Cancelar</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Templates Salvos</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : list.length === 0 ? (
            <div className="text-center py-10 px-4 bg-muted/20 rounded-lg border border-dashed">
              <p className="text-sm text-muted-foreground">Nenhum template cadastrado ainda.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((t) => (
                <div key={t.id} className="p-3 border rounded-lg group">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{t.name}</h4>
                      <p className="text-xs text-muted-foreground">Gatilho: {t.trigger_event}</p>
                    </div>
                    <Switch checked={t.is_active} onCheckedChange={async (c) => { await _tog({ data: { id: t.id, is_active: c } }); refresh(); }} />
                  </div>
                  <p className="text-sm bg-muted/50 p-2 rounded mt-2 whitespace-pre-wrap">{t.message}</p>
                  <div className="flex gap-1 mt-2 justify-end">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingId(t.id); setForm({ name: t.name, message: t.message, trigger_event: t.trigger_event, target_audience: t.target_audience || "ALL", is_active: t.is_active, stop_condition: (t.stop_condition || "envio_unico") as any, stop_max_sends: t.stop_max_sends ?? 3, stop_date: t.stop_date ? String(t.stop_date).slice(0,10) : "" }); }}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                      onClick={async () => { if (confirm("Excluir?")) { await _del({ data: { id: t.id } }); refresh(); } }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ CONFIG ============
function ConfigTab() {
  const _get = useServerFn(getWhatsappConfig);
  const _save = useServerFn(saveWhatsappConfig);
  const _check = useServerFn(checkInstanceStatus);
  const _connect = useServerFn(connectInstance);
  const _disconnect = useServerFn(disconnectInstance);
  const _webhook = useServerFn(setEvolutionWebhook);
  const _webhookGet = useServerFn(getEvolutionWebhook);
  const _cleanup = useServerFn(cleanupOtherInstances);
  const _fetchQr = useServerFn(fetchQrCode);
  const _reset = useServerFn(resetInstance);
  const [qrSeconds, setQrSeconds] = useState<number>(30);


  const [cfg, setCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [showUrl, setShowUrl] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [form, setForm] = useState({ baseUrl: "", globalApiKey: "", instanceName: "" });
  const [polling, setPolling] = useState(false);
  const [diag, setDiag] = useState<{ rawState?: string; ownerNumber?: string | null; deviceName?: string | null; lastStatusCheck?: string | null } | null>(null);
  const [stuck, setStuck] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState<any>(null);
  const _verifyPin = useServerFn(verifyAdminPin);
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const onUnlock = async () => {
    if (!/^\d{4,6}$/.test(pin)) { toast.error("Informe o PIN do proprietário."); return; }
    setUnlocking(true);
    try {
      await _verifyPin({ data: { pin } });
      setUnlocked(true);
      setPin("");
      toast.success("Área liberada");
    } catch (e: any) {
      toast.error(e?.message || "PIN inválido");
    } finally {
      setUnlocking(false);
    }
  };

  const lockCard = (title: string, description: string) => (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Lock className="h-4 w-4" /> {title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <Input type="password" inputMode="numeric" maxLength={6} className="max-w-[180px]" placeholder="PIN do proprietário"
          value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
        <Button variant="secondary" onClick={onUnlock} disabled={unlocking}>
          {unlocking ? "Validando..." : "Desbloquear"}
        </Button>
      </CardContent>
    </Card>
  );


  const refresh = async () => {
    try {
      const data = (await _get()) as any;
      setCfg(data);
      setForm({ baseUrl: data.baseUrl, globalApiKey: data.globalApiKey, instanceName: data.instanceName });
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const run = async (key: string, fn: () => Promise<any>, okMsg?: string) => {
    setBusy(key);
    let ok = false;
    let result: any = null;
    try {
      result = await fn();
      ok = true;
    } catch (e: any) {
      toast.error(e?.message || "Erro inesperado", { duration: 8000 });
    } finally {
      setBusy(null);
      await refresh();
    }
    if (ok && okMsg) toast.success(okMsg);
    return result;
  };

  // Polling de QR enquanto estiver conectando e sem QR ainda
  useEffect(() => {
    if (!cfg) return;
    const needsPoll =
      cfg.connectionStatus === "connecting" && !cfg.qrCode && !polling;
    if (!needsPoll) return;
    setPolling(true);
    let attempts = 0;
    const id = setInterval(async () => {
      attempts++;
      try {
        const r: any = await _fetchQr();
        if (r?.qrCode || r?.status === "connected") {
          clearInterval(id);
          setPolling(false);
          await refresh();
        }
      } catch {}
      if (attempts >= 10) {
        clearInterval(id);
        setPolling(false);
      }
    }, 3000);
    return () => { clearInterval(id); setPolling(false); };
  }, [cfg?.connectionStatus, cfg?.qrCode]);

  // Renova o QR a cada 30s enquanto ele estiver visível (o QR da Evolution expira ~40s)
  useEffect(() => {
    if (!cfg?.qrCode || cfg?.connectionStatus === "connected") return;
    setQrSeconds(30);
    const tick = setInterval(() => setQrSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    const renew = setInterval(async () => {
      try {
        await _connect();
        await refresh();
        setQrSeconds(30);
      } catch {}
    }, 30000);
    return () => { clearInterval(tick); clearInterval(renew); };
  }, [cfg?.qrCode, cfg?.connectionStatus]);

  // Após a leitura, confirma a conexão sozinho (3s por até 1 min)
  useEffect(() => {
    if (!cfg?.qrCode || cfg?.connectionStatus === "connected") return;
    let tries = 0;
    setStuck(false);
    const id = setInterval(async () => {
      tries++;
      try {
        const r: any = await _check();
        setDiag({
          rawState: r?.rawState,
          ownerNumber: r?.ownerNumber,
          deviceName: r?.deviceName,
          lastStatusCheck: r?.lastStatusCheck,
        });
        if (r?.status === "connected") {
          clearInterval(id);
          setStuck(false);
          await refresh();
          toast.success("WhatsApp conectado!");
          return;
        }
      } catch {}
      if (tries >= 20) {
        clearInterval(id);
        setStuck(true);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [cfg?.qrCode, cfg?.connectionStatus]);

  const handleConnect = async () => {
    const r: any = await run("connect", () => _connect());
    if (r?.alreadyConnected) {
      toast.success("Instância já estava conectada.");
    } else if (r && !r.qrCode) {
      toast.message("QR não recebido ainda — buscando novamente…", { duration: 5000 });
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Isso apaga e recria a instância deste centro de treinamento e gera um novo QR Code. A ação é bloqueada automaticamente se a instância estiver compartilhada com outro centro. Continuar?")) return;
    await run("reset", () => _reset(), "Instância resetada — leia o novo QR Code");
  };


  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const connected = cfg?.connectionStatus === "connected";

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {!unlocked ? (
        lockCard(
          "Credenciais Evolution API",
          "Área protegida. Informe o PIN do proprietário para configurar as credenciais.",
        )
      ) : (
      <Card>

        <CardHeader>
          <CardTitle>Credenciais Evolution API</CardTitle>
          <CardDescription>Configure os dados de acesso ao seu servidor da Evolution API.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>URL da API (Base URL)</Label>
            <div className="relative">
              <Input type={showUrl ? "text" : "password"} value={form.baseUrl}
                onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} placeholder="https://api.exemplo.com" />
              <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setShowUrl(!showUrl)}>{showUrl ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Global API Key</Label>
            <div className="relative">
              <Input type={showKey ? "text" : "password"} value={form.globalApiKey}
                onChange={(e) => setForm({ ...form, globalApiKey: e.target.value })} />
              <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setShowKey(!showKey)}>{showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Nome da Instância</Label>
            <Input value={form.instanceName}
              onChange={(e) => setForm({ ...form, instanceName: e.target.value })} placeholder="minhaInstancia" />
            <p className="text-xs text-muted-foreground">
              Use apenas letras minúsculas e sem espaços. O nome precisa ser <b>exclusivo deste centro</b>:
              dois centros nunca podem usar a mesma instância no mesmo servidor, senão um derruba a conexão do outro.
            </p>

          </div>
          <Button className="w-full" disabled={busy === "save"}
            onClick={() => run("save", () => _save({ data: form }), "Configurações salvas")}>
            {busy === "save" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Salvar Configurações
          </Button>
          <Button variant="outline" className="w-full text-destructive" disabled={busy === "cleanup"}
            onClick={() => run("cleanup", async () => {
              if (!window.confirm("Remove apenas instâncias órfãs do servidor. As instâncias de outros centros de treinamento cadastrados no app são preservadas. Continuar?")) return;
              const r: any = await _cleanup();
              toast.success(`${r.removed} instância(s) órfã(s) removida(s) · ${r.skipped} preservada(s)`);
            })}>
            <Trash2 className="w-4 h-4 mr-2" /> Limpar Instâncias Órfãs
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Remove somente instâncias que nenhum centro de treinamento usa. As instâncias dos outros centros nunca são apagadas.
          </p>

        </CardContent>
      </Card>
      )}


      <Card>
        <CardHeader>
          <CardTitle>Conexão do Aparelho</CardTitle>
          <CardDescription>Leia o QR Code com o WhatsApp que fará os envios de mensagens.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          {!connected && cfg?.hasConfig && (
            <p className="text-xs text-muted-foreground bg-muted/40 rounded p-2 text-left">
              Atenção: se essa instância já estava conectada em outro sistema, desconecte lá antes ou clique em <b>Desconectar / Resetar Instância</b> abaixo para liberar a sessão.
            </p>
          )}
          {cfg?.hasConfig && diag && (
            <div className="text-xs text-left bg-muted/40 rounded p-2 space-y-0.5">
              <div>
                Instância: <b>{cfg.instanceName}</b>
              </div>
              <div>
                Estado no servidor: <b>{diag.rawState || "desconhecido"}</b>
              </div>
              <div>
                Número vinculado: <b>{diag.ownerNumber ? `+${diag.ownerNumber}` : "nenhum"}</b>
              </div>
              <div>
                Nome do aparelho: <b>{diag.deviceName || "—"}</b>
              </div>
              {diag.lastStatusCheck && (
                <div className="text-muted-foreground">
                  Última verificação: {new Date(diag.lastStatusCheck).toLocaleTimeString("pt-BR")}
                </div>
              )}
            </div>
          )}
          {connected ? (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 border-2 border-green-500 flex items-center justify-center">
                <Power className="w-8 h-8 text-green-600" />
              </div>
              <p className="font-semibold">WhatsApp Conectado</p>
            </>
          ) : cfg?.qrCode ? (
            <>
              <img src={cfg.qrCode.startsWith("data:") ? cfg.qrCode : `data:image/png;base64,${cfg.qrCode}`} alt="QR" className="mx-auto w-56 h-56" />
              <p className="text-xs text-muted-foreground">Abra o WhatsApp &gt; Aparelhos conectados &gt; Conectar aparelho</p>
              <p className="text-xs text-muted-foreground">
                O QR Code é renovado automaticamente em {qrSeconds}s — leia sempre o código que está na tela.
              </p>
              <div className="rounded-lg border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-4 text-left">
                <p className="text-base font-bold uppercase tracking-wide text-amber-900 dark:text-amber-200">
                  ⚠️ Atenção
                </p>
                <p className="mt-2 text-sm font-semibold text-amber-900 dark:text-amber-100">
                  Após aparecer no celular a mensagem de "dispositivo conectado", clique em OK no celular. A conexão é confirmada aqui automaticamente em alguns segundos.
                </p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3 text-left text-xs space-y-1">
                <p className="font-semibold text-sm">Como ler o QR Code</p>
                <p>
                  Funciona igual no <b>WhatsApp comum</b> e no <b>WhatsApp Business</b> — não faz
                  diferença. Abra o app do número que você quer conectar &gt; Configurações &gt;{" "}
                  <b>Aparelhos conectados</b> &gt; Conectar aparelho.
                </p>
                <p>
                  Se os dois apps estiverem no mesmo celular, leia o QR pelo app que tem o número
                  desejado — cada app tem sua própria lista de aparelhos conectados.
                </p>
                <p>
                  <b>WhatsApp Web e esta API podem funcionar juntos</b>: cada um ocupa um slot de aparelho
                  conectado. O limite total é 4 aparelhos por número. Se estourar, remova um aparelho
                  antigo no celular antes de ler o QR.
                </p>
                <p>
                  O mesmo número não pode ficar em duas instâncias da API ao mesmo tempo.
                </p>
              </div>
              {stuck && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-left text-xs space-y-2">
                  <p className="font-semibold">A leitura não foi confirmada — possível sessão presa.</p>
                  <p>
                    No celular, em <b>Aparelhos conectados</b>, procure o item com nome <b>"{diag?.deviceName || "desconhecido"}"</b> (ou um dispositivo desconectado relacionado a esta conexão) e remova-o. Depois clique em <b>"Gerar outro QR"</b> abaixo e leia o novo código em até 30s.
                  </p>
                  <p className="text-muted-foreground">
                    Dica: o WhatsApp Web do seu escritório pode continuar conectado normalmente. Ele e esta API ocupam slots diferentes, desde que cada um tenha seu próprio aparelho na lista.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={busy === "connect" || polling}
                    onClick={handleConnect}
                  >
                    {(busy === "connect" || polling) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Gerar outro QR
                  </Button>
                </div>
              )}
            </>


          ) : (
            <div className="py-8">
              <p className="text-sm text-muted-foreground mb-4">
                {polling ? "Aguardando QR Code..." : "Aparelho desconectado."}
              </p>
              <Button onClick={handleConnect}
                disabled={busy === "connect" || !cfg?.hasConfig || polling}>
                {(busy === "connect" || polling) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Gerar QR Code
              </Button>
            </div>
          )}
          {!connected && cfg?.hasConfig && (
            <>
              <Button variant="outline" className="w-full" disabled={busy === "reset"} onClick={handleReset}>
                {busy === "reset" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Resetar instância e gerar novo QR
              </Button>
              <p className="text-[11px] text-muted-foreground text-left">
                Apaga e recria apenas a instância deste centro de treinamento. Não afeta os outros centros.
              </p>
            </>
          )}
          <p className="text-[11px] text-muted-foreground text-left bg-muted/40 rounded p-2">
            O WhatsApp mostrará o aparelho conectado como um servidor localizado nos EUA (São Francisco). Isso é esperado: é o local do servidor da API, não um acesso de terceiros às suas conversas.
          </p>

          <div className="space-y-2">
            <Button variant="outline" className="w-full" disabled={busy === "check"}
              onClick={() => run("check", async () => {
                const r: any = await _check();
                setDiag({
                  rawState: r?.rawState,
                  ownerNumber: r?.ownerNumber,
                  deviceName: r?.deviceName,
                  lastStatusCheck: r?.lastStatusCheck,
                });
                toast.success(`Status: ${r?.status} (${r?.rawState || "?"})`);
              })}>
              <RefreshCw className={`w-4 h-4 mr-2 ${busy === "check" ? "animate-spin" : ""}`} /> Verificar Status
            </Button>
            {!unlocked ? (
              <p className="text-xs text-muted-foreground">
                Webhooks e reset da instância exigem o PIN do proprietário (desbloqueie na seção ao lado).
              </p>
            ) : (
              <>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" disabled={busy === "webhook-pub"}
                onClick={() => run("webhook-pub", () => _webhook({ data: { target: "published" } }), "Webhook publicado configurado")}>
                <Webhook className="w-4 h-4 mr-2" /> Webhook (Publicado)
              </Button>
              <Button variant="outline" disabled={busy === "webhook-prev"}
                onClick={() => run("webhook-prev", () => _webhook({ data: { target: "preview" } }), "Webhook preview configurado")}>
                <Webhook className="w-4 h-4 mr-2" /> Webhook (Preview)
              </Button>
            </div>
            <Button variant="outline" className="w-full" disabled={busy === "webhook-get"}
              onClick={() => run("webhook-get", async () => { const r = await _webhookGet(); setWebhookInfo(r); }, "Webhook atual carregado")}>
              <RefreshCw className={`w-4 h-4 mr-2 ${busy === "webhook-get" ? "animate-spin" : ""}`} /> Ver webhook atual na Evolution
            </Button>
            {webhookInfo && (
              <div className="text-xs bg-muted/50 rounded p-2 space-y-1 break-all">
                <div><strong>URL:</strong> {webhookInfo.url || "(vazio)"}</div>
                <div><strong>Enabled:</strong> {String(webhookInfo.enabled)}</div>
                <div><strong>Eventos:</strong> {(webhookInfo.events || []).join(", ") || "(nenhum)"}</div>
                <div><strong>Base64:</strong> {String(webhookInfo.webhookBase64)} · <strong>ByEvents:</strong> {String(webhookInfo.webhookByEvents)}</div>
              </div>
            )}
            <Button variant="outline" className="w-full text-destructive" disabled={busy === "disc"}
              onClick={() => run("disc", () => _disconnect(), "Desconectado")}>
              <Power className="w-4 h-4 mr-2" /> Desconectar / Resetar Instância
            </Button>
              </>
            )}

          </div>
        </CardContent>
      </Card>
    </div>
  );
}
